import { nanoid, customAlphabet } from "nanoid";

const DEFAULT_CHARACTER_SET = "0123456789abcdefghijklmnopqrstuvwxyz";
const DEFAULT_ID_LENGTH = 10;

export const generateRandomId = () => {
  return `NEW_${nanoid(10)}`;
};

// Generate URL friendly id
export const generateRandomIdV2 = ({
  characters = DEFAULT_CHARACTER_SET,
  length = DEFAULT_ID_LENGTH,
} = {}) => {
  const customNanoId = customAlphabet(characters, length);
  return customNanoId();
};

export const generateRandomNumber = (min, max, float = false) => {
  try {
    // Both min max are inclusive
    const byteArray = new Uint8Array(1);
    crypto.getRandomValues(byteArray);

    const randomValue = byteArray[0] / 256; // Divide by maximum 8-bit value + 1

    if (float) {
      return Number(min + randomValue * (max - min)).toFixed(2);
    } else {
      return Math.floor(min + randomValue * (max - min + 1));
    }
  } catch (e) {
    return min;
  }
};

/**
 * Generates a cryptographically secure random nonce string
 * @param {number} length - The length of the nonce (default: 32)
 * @param {string} encoding - The encoding format: 'hex', 'base64', or 'base64url' (default: 'hex')
 * @returns {string} A random nonce string
 */
export const generateRandomNonce = (length = 32, encoding = "hex") => {
  try {
    // Generate random bytes using Web Crypto API
    const randomBytes = new Uint8Array(length);
    crypto.getRandomValues(randomBytes);

    switch (encoding) {
      case "base64":
        // Convert to base64
        return btoa(String.fromCharCode(...randomBytes));

      case "base64url":
        // Convert to base64url (URL-safe base64)
        return btoa(String.fromCharCode(...randomBytes))
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=/g, "");

      case "hex":
      default:
        // Convert to hexadecimal
        return Array.from(randomBytes)
          .map(byte => byte.toString(16).padStart(2, "0"))
          .join("");
    }
  } catch (error) {
    // Fallback to nanoid if Web Crypto API is not available
    return nanoid(length);
  }
};
